import type { ComponentPropsWithRef, ReactNode } from "react";

import { rainbowRingClass } from "../../rainbow-ring";

/**
 * The house pill button — the one place the action-button styling lives, so
 * every in-page action reads identically ("fix once, fix everywhere"). For
 * navigation use {@link ActionLink}; for filter/form submits with their own
 * logic use FilterSubmit / SubmitButton. Variants: `outline` (default), `solid`
 * (primary), and the status colors (owner, 2026-07-05: red = danger, amber =
 * warning, emerald = approve) — colored at rest so the stakes read before the
 * pointer gets there. Sizes: `md` (h-9, default), `sm` (compact), and `lg`
 * (the big primary submit at the foot of a CRUD form).
 */
export type PillVariant =
  | "outline"
  | "solid"
  | "danger"
  | "warning"
  | "approve"
  /** Red FILLED, for the one irreversible action a surface exists to ask
   *  about — a confirmation's own button, never a page's toolbar. */
  | "danger-solid";
export type PillSize = "sm" | "md" | "lg" | "icon";

/** The house action-pill classes — the impl detail of {@link Button}, also
 *  composed by the submit-button family (WriteSubmit / ConfirmButton), which
 *  layer the rainbow busy ring themselves. Pages compose those components —
 *  never this string. */
export function pillClass(
  variant: PillVariant = "outline",
  size: PillSize = "md",
): string {
  // Cursor / disabled-grey / the rainbow busy ring all come from rainbowRingClass
  // (shared with SubmitButton), so they're NOT baked in here.
  const base =
    "inline-flex shrink-0 items-center justify-center rounded-full font-mono";
  // The text sizes carry `pb-0.5`: the mono face's words sit about a pixel
  // under the middle of their line box, so a label rode low in the pill. Two
  // pixels of bottom padding lift the line exactly one. A whole pixel, because
  // the engine snaps a baseline to the pixel grid: half of one moves the text
  // either way, by where the pill happens to sit. The icon size has none; its
  // glyph is drawn on the box's own center.
  const sizes: Record<PillSize, string> = {
    sm: "h-7 px-3 pb-0.5 text-[11px]",
    md: "h-9 px-4 pb-0.5 text-xs",
    lg: "h-12 px-6 pb-0.5 text-sm",
    icon: "relative h-6 w-6 p-0 text-sm leading-none",
  };
  // Hovers are `enabled:` so a disabled OR busy button (the disabled attr is set
  // while busy) doesn't react — e.g. the danger button won't brighten mid-run.
  const variants: Record<PillVariant, string> = {
    outline:
      "border border-line text-fg transition-colors enabled:hover:border-fg-subtle",
    solid: "bg-fg text-bg transition-opacity enabled:hover:opacity-80",
    danger:
      "border border-red-600/40 text-red-400 transition-colors enabled:hover:border-red-500",
    "danger-solid":
      "bg-red-600 text-white transition-opacity enabled:hover:opacity-80",
    warning:
      "border border-amber-600/40 text-amber-400 transition-colors enabled:hover:border-amber-500",
    approve:
      "border border-emerald-600/40 text-emerald-400 transition-colors enabled:hover:border-emerald-500",
  };
  return `${base} ${sizes[size]} ${variants[variant]}`;
}

/** The strokes of an icon pill's ×, +, −, ↺, ⏸, ▶ and ⊘, around their own
 *  center: six px of ink, the size the mono face draws them at the pill's text
 *  size. The ↺ is an arc round the center, open at the top, with its head at
 *  the end that travels anticlockwise. The ▶ sits between its box's center
 *  and its centroid, where the eye puts a triangle's middle. */
const GLYPH_STROKES = new Map([
  ["×", "M-3 -3l6 6M3 -3l-6 6"],
  ["+", "M0 -3v6M-3 0h6"],
  ["−", "M-3 0h6"],
  ["-", "M-3 0h6"],
  ["↺", "M-1.8 -2.7A3 3 0 1 0 1.2 -2.7M1.88 -.82L1.2 -2.7L3.17 -3.05"],
  ["⏸", "M-1.5 -3v6M1.5 -3v6"],
  ["▶", "M-2.1 -3L3 0L-2.1 3Z"],
  ["⊘", "M3 0A3 3 0 1 1 -3 0A3 3 0 1 1 3 0M-2.1 2.1L2.1 -2.1"],
]);

/**
 * What an icon pill shows for its label. A glyph in {@link GLYPH_STROKES} is
 * drawn, not typed: a font's operators sit where its metrics put them, and
 * engines round those differently (WebKit lands them visibly off a 24px
 * circle's center). Nor is
 * it a small svg flex-centered in the pill, which iPad Safari still drew half
 * a pixel off: the svg IS the pill's box and the strokes sit on its 50%/50%
 * point, so ring and glyph are placed from one box. The typed glyph stays in the
 * markup for a screen reader; any other label, and any other size, passes
 * through. Shared by {@link Button} and the submit-button family.
 */
export function pillContent(size: PillSize, children: ReactNode): ReactNode {
  if (size !== "icon" || typeof children !== "string") return children;
  const strokes = GLYPH_STROKES.get(children.trim());
  if (!strokes) return children;
  return (
    <>
      <span className="sr-only">{children}</span>
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.1"
      >
        <svg x="50%" y="50%" overflow="visible">
          <path d={strokes} />
        </svg>
      </svg>
    </>
  );
}

export function Button({
  variant = "outline",
  size = "md",
  className = "",
  type = "button",
  busy,
  disabled,
  href,
  download,
  children,
  ...props
}: ComponentPropsWithRef<"button"> & {
  variant?: PillVariant;
  size?: PillSize;
  /** Run the shared rainbow loading ring while an action is in flight (same cue
   *  as SubmitButton) and block re-clicks. The ring is the cue — it never dims
   *  the button. Omit for a plain button. */
  busy?: boolean;
  /** Render the pill as a link — e.g. a file download — instead of a button.
   *  While `disabled` (or `busy`) it falls back to an inert `<button disabled>`,
   *  since a native `<a>` can't be disabled — so the disabled state always looks
   *  and behaves identically to a regular button ("fix once, fix everywhere"). */
  href?: string;
  download?: ComponentPropsWithRef<"a">["download"];
}) {
  // The ring / cursor / disabled-grey come from the shared helper (same as
  // SubmitButton) — busy shows the ring in full colour, never the grey.
  const cls = `${pillClass(variant, size)} ${rainbowRingClass({
    solid: variant === "solid" || variant === "danger-solid",
    busy: !!busy,
    disabled: !!disabled,
  })} ${className}`.trim();

  // An enabled link renders as an <a> (so `download` / navigation works); a
  // disabled or busy link — or any non-link action — is an inert <button>.
  if (href !== undefined && !disabled && !busy) {
    // The pill hovers are `enabled:` — a pseudo-class only button elements
    // match — so anchors need the plain hover classes. An <a> here is by
    // definition enabled (disabled links render as <button disabled>).
    const linkHover: Record<PillVariant, string> = {
      outline: "hover:border-fg-subtle",
      solid: "hover:opacity-80",
      danger: "hover:border-red-500",
      "danger-solid": "hover:opacity-80",
      warning: "hover:border-amber-500",
      approve: "hover:border-emerald-500",
    };
    return (
      <a
        href={href}
        download={download}
        className={`${cls} ${linkHover[variant]}`}
        {...(props as ComponentPropsWithRef<"a">)}
      >
        {pillContent(size, children)}
      </a>
    );
  }
  return (
    <button
      type={type}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      className={cls}
      {...props}
    >
      {pillContent(size, children)}
    </button>
  );
}

/**
 * The bar button: the control in the header bar of an app-like card — the
 * calendar's view and kind menus, the inbox's mark read and delete. A filled
 * rounded rectangle in the text face, semibold, where the house pill is an
 * outlined capsule in mono: a bar of these reads as the card's own chrome,
 * and a pill there reads as something dropped onto it. Pages still take
 * {@link Button}; this is for a card that has a bar.
 *
 * `toolbarButtonClass` is the look alone, for a trigger that is its own
 * element (a menu button with a popover behind it).
 */
export type ToolbarButtonVariant = "default" | "danger";

export function toolbarButtonClass(
  variant: ToolbarButtonVariant = "default",
): string {
  return `flex items-center gap-x-1.5 rounded-md bg-fg/10 px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-fg/5 transition-colors enabled:hover:bg-fg/15 ${
    variant === "danger" ? "text-red-400" : "text-fg"
  }`;
}

export function ToolbarButton({
  variant = "default",
  className = "",
  type = "button",
  busy,
  disabled,
  children,
  ...props
}: ComponentPropsWithRef<"button"> & {
  variant?: ToolbarButtonVariant;
  /** The action is in flight: the shared rainbow ring runs on the button,
   *  which never dims, and a second press does nothing. */
  busy?: boolean;
}) {
  return (
    <button
      type={type}
      aria-busy={busy || undefined}
      disabled={disabled || busy}
      className={`${toolbarButtonClass(variant)} ${rainbowRingClass({
        busy: !!busy,
        disabled: !!disabled,
      })} ${className}`.trim()}
      {...props}
    >
      {children}
    </button>
  );
}
